// Offline store — architecture §3.1, App Flow §7.3: drafts, queued actions, cached editions.
import Dexie, { type Table } from 'dexie';

export interface DraftRecord {
  /** contribution id, or `new:<prompt_id>` before the server has one. */
  key: string;
  promptId: string;
  familyId: string;
  contributionId: string | null;
  text: string;
  updatedAt: number;
}

export interface QueuedAction {
  id?: number;
  kind: string;
  payload: unknown;
  createdAt: number;
  attempts: number;
}

export interface CachedDoc {
  key: string;
  value: unknown;
  cachedAt: number;
}

class RootsDb extends Dexie {
  drafts!: Table<DraftRecord, string>;
  queue!: Table<QueuedAction, number>;
  cache!: Table<CachedDoc, string>;

  constructor() {
    super('roots');
    this.version(1).stores({
      drafts: '&key, promptId, updatedAt',
      queue: '++id, kind, createdAt',
      cache: '&key, cachedAt',
    });
  }
}

export const db = new RootsDb();

export async function saveDraft(draft: Omit<DraftRecord, 'updatedAt'>) {
  await db.drafts.put({ ...draft, updatedAt: Date.now() });
}

export async function getDraft(key: string) {
  return db.drafts.get(key);
}

export async function listDrafts() {
  return db.drafts.toArray();
}

export async function clearDraft(key: string) {
  await db.drafts.delete(key);
}

export async function queueAction(kind: string, payload: unknown) {
  await db.queue.add({ kind, payload, createdAt: Date.now(), attempts: 0 });
}

export async function cacheDoc(key: string, value: unknown) {
  await db.cache.put({ key, value, cachedAt: Date.now() });
}

export async function readCached<T>(key: string): Promise<T | null> {
  const row = await db.cache.get(key);
  return row ? (row.value as T) : null;
}

/** Replays queued submits, hearts and edits when the connection returns. */
export async function drainQueue(handlers: Record<string, (payload: never) => Promise<unknown>>) {
  const items = await db.queue.orderBy('createdAt').toArray();
  for (const item of items) {
    const handler = handlers[item.kind];
    if (!handler) {
      if (item.id !== undefined) await db.queue.delete(item.id);
      continue;
    }
    try {
      await handler(item.payload as never);
      if (item.id !== undefined) await db.queue.delete(item.id);
    } catch {
      if (item.id !== undefined) {
        await db.queue.update(item.id, { attempts: item.attempts + 1 });
        // Give up after ten tries so a poisoned action never blocks the rest.
        if (item.attempts + 1 >= 10) await db.queue.delete(item.id);
      }
    }
  }
}

export async function queueSize() {
  return db.queue.count();
}
